import React from 'react';
import CreatableSelect from 'react-select/creatable';
import { formatPrice, getPileExtraPrice, getKapsulUnitPrice, getTulAltEtekKatlamaPrice, calculateAstarPrice, calculateOzelTasarimTotal } from '../../../../utils';
import { handleNumericKeyDown, customSelectStyles } from '../OrderFormUtils';

const TulKanatForm = ({
  newOrder,
  onFieldChange,
  isLoading,
  productItems,
  productOptions,
  handleProductSelect,
  localPileModels,
  adminSettings,
  selectedProductType,
  handleAddAccessory,
  handleAccessoryChange,
  handleRemoveAccessory,
  handleOzelProductSelect,
  handleAstarProductSelect
}) => {
  const isTul = selectedProductType === 'tul';
  const isOzelTasarimActive = !!(newOrder.hasOzelTasarim || newOrder.ozelProductName);
  const isAstarActive = !!(newOrder.hasAstar || newOrder.astarProductName);

  // Kapsül birim fiyatını adminSettings'ten çek (Ana ürün için)
  const kapsulBirimFiyat = getKapsulUnitPrice(adminSettings, selectedProductType);
  const altEtekKatlamaFiyat = getTulAltEtekKatlamaPrice(adminSettings);

  // Ana ürün filtreleme
  const mainProductOptions = isTul
    ? productOptions.filter(o => {
        const item = productItems.find(p => p.id === o.value);
        return item ? item.productType === 'tul' : true;
      })
    : productOptions.filter(o => {
        const item = productItems.find(p => p.id === o.value);
        return item ? item.productType === 'kanat' : true;
      });

  const astarProductOptions = productOptions.filter(o => {
    const item = productItems.find(p => p.id === o.value);
    return item ? item.productType === 'kanat' : true;
  });

  const kanatQuantity = parseFloat(newOrder.quantity) || 1;
  const handleAltEtekKatlamaChange = (checked) => {
    onFieldChange('altEtekKatlama', checked);
    const base = parseFloat(newOrder.baseUnitPrice) || 0;
    let pileExtra = getPileExtraPrice(adminSettings, selectedProductType, newOrder.pileModel);
    const etekExtra = checked ? altEtekKatlamaFiyat : 0;
    onFieldChange('unitPrice', base + pileExtra + etekExtra);
  };

  return (
    <>
      <div className="form-group">
        <label>Oda Adı *</label>
        <input
          type="text"
          value={newOrder.roomName || ''}
          onChange={(e) => onFieldChange('roomName', e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label>Ürün Adı *</label>
        <CreatableSelect
          options={mainProductOptions}
          value={
            mainProductOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
            (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
          }
          onChange={(option) => {
            if (!option) {
              onFieldChange('productName', '');
              onFieldChange('unitPrice', '');
            } else if (option.__isNew__) {
              onFieldChange('productName', option.value);
              onFieldChange('unitPrice', '');
            } else {
              handleProductSelect(option.value);
            }
          }}
          isDisabled={isLoading}
          isClearable
          isSearchable
          placeholder="Ürün seçin veya yazın..."
          noOptionsMessage={() => "Ürün bulunamadı"}
          formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
          styles={customSelectStyles}
        />
      </div>

      <div className="form-group">
        <label>Ürün Varyantı</label>
        <input
          type="text"
          value={newOrder.productVariant || ''}
          onChange={(e) => onFieldChange('productVariant', e.target.value)}
          disabled={isLoading}
          placeholder="Renk, desen vb. giriniz..."
        />
      </div>

      <div className="form-group">
        <label>En (cm) *</label>
        <input
          type="number"
          step="0.01"
          value={newOrder.width || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('width', e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label>Boy (cm) *</label>
        <input
          type="number"
          step="0.01"
          value={newOrder.height || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('height', e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label>Birim Fiyat (₺) *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={newOrder.unitPrice || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('unitPrice', e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label>Pile Modeli *</label>
        <select
          value={newOrder.pileModel || ''}
          onChange={(e) => {
            const selectedModel = e.target.value;
            onFieldChange('pileModel', selectedModel);

            if (selectedModel === 'Kapsül Pile' && kapsulBirimFiyat) {
              onFieldChange('capsuleUnitPrice', kapsulBirimFiyat);
            } else {
              onFieldChange('capsuleUnitPrice', '');
            }

            if (newOrder.baseUnitPrice !== undefined) {
              const extra = getPileExtraPrice(adminSettings, selectedProductType, selectedModel);
              const etekExtra = isTul && newOrder.altEtekKatlama ? altEtekKatlamaFiyat : 0;
              onFieldChange('unitPrice', Number(newOrder.baseUnitPrice) + extra + etekExtra);
            }
          }}
          required
          disabled={isLoading}
        >
          <option value="">Pile modeli seçin</option>
          {localPileModels.map((model) => (
            <option key={model.id} value={model.modelName}>
              {model.modelName}
            </option>
          ))}
        </select>
      </div>

      {newOrder.pileModel && (
        <div className="form-group">
          <label>Pile Sıklığı</label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            value={newOrder.pileDensity || ''}
            onChange={(e) => onFieldChange('pileDensity', e.target.value)}
            disabled={isLoading}
            placeholder="Pile sıklığı"
          />
          <small>Pile sıklığını düzenleyebilirsiniz</small>
        </div>
      )}

      {newOrder.width && newOrder.usedM2 && (
        <div className="form-group">
          <label>Kullanılan Mt</label>
          <input
            type="text"
            value={newOrder.usedM2}
            readOnly
            className="readonly-field"
          />
        </div>
      )}

      <div className="form-group">
        <label>Ek Ücret (₺)</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={newOrder.extraPrice || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('extraPrice', e.target.value)}
          disabled={isLoading}
        />
      </div>

      {newOrder.pileModel === 'Kapsül Pile' && (
        <>
          <div className="form-group">
            <label>Kapsül Birim Fiyat (₺) *</label>
            <input
              type="number"
              step="0.01"
              value={newOrder.capsuleUnitPrice && newOrder.capsuleUnitPrice !== 0 && newOrder.capsuleUnitPrice !== '0' ? newOrder.capsuleUnitPrice : ''}
              onChange={(e) => onFieldChange('capsuleUnitPrice', e.target.value)}
              required
              disabled={isLoading}
              placeholder="0.00"
            />
            {kapsulBirimFiyat > 0 && (
              <small style={{ color: '#888' }}>Ürün listesindeki fiyat: {formatPrice(kapsulBirimFiyat)} ₺</small>
            )}
          </div>

          {newOrder.width && newOrder.capsulePrice && newOrder.capsulePrice !== 0 && newOrder.capsulePrice !== '0' && (
            <div className="form-group">
              <label>Kapsül Ücreti (₺)</label>
              <input
                type="text"
                value={formatPrice(newOrder.capsulePrice)}
                readOnly
                className="readonly-field"
              />
            </div>
          )}
        </>
      )}

      <div className="form-group">
        <label>Adet *</label>
        <input
          type="number"
          min="1"
          value={newOrder.quantity || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('quantity', e.target.value)}
          required
          disabled={isLoading}
          placeholder="Adet giriniz"
        />
      </div>

      {/* Alt Etek Katlama — sadece Tül siparişlerinde */}
      {isTul && (
        <div className="form-group">
          <label>Alt Etek Katlama</label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            minHeight: '42px',
            padding: '0 10px',
            border: '2px solid #e1e5e9',
            borderRadius: '6px',
            background: 'white',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
            onClick={() => !isLoading && handleAltEtekKatlamaChange(!newOrder.altEtekKatlama)}
          >
            <input
              type="checkbox"
              checked={!!newOrder.altEtekKatlama}
              onChange={(e) => handleAltEtekKatlamaChange(e.target.checked)}
              disabled={isLoading}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '18px', height: '18px', cursor: isLoading ? 'not-allowed' : 'pointer', flexShrink: 0, accentColor: 'var(--primary-600)', margin: 0 }}
            />
            <span style={{ fontSize: '0.9rem', color: '#333', userSelect: 'none' }}>
              {altEtekKatlamaFiyat > 0 ? `Evet (+${formatPrice(altEtekKatlamaFiyat)} ₺)` : 'Evet'}
            </span>
          </div>
        </div>
      )}

      {/* ÖZEL TASARIM ACCORDION — Sadece Kanat siparişlerinde */}
      {!isTul && (
        <div style={{ gridColumn: '1 / -1', marginTop: '10px', marginBottom: '10px' }}>
          <div 
            onClick={() => onFieldChange('hasOzelTasarim', !isOzelTasarimActive)}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '12px 15px', 
              backgroundColor: isOzelTasarimActive ? 'var(--primary-600)' : '#f1f5f9', 
              color: isOzelTasarimActive ? 'white' : '#1e293b', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontWeight: '600', 
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-magic"></i>
              <span>Özel Tasarım Kanat</span>
            </div>
            <i className={`fas fa-chevron-${isOzelTasarimActive ? 'up' : 'down'}`}></i>
          </div>

          {isOzelTasarimActive && (
            <div 
              className="dynamic-fields"
              style={{ 
                padding: '20px 15px', 
                border: '1px solid #cbd5e1', 
                borderTop: 'none', 
                borderRadius: '0 0 8px 8px', 
                backgroundColor: '#fff',
                marginTop: 0
              }}
            >
              <div className="form-group">
                <label>Özel Ürün Adı</label>
                <CreatableSelect
                  options={productOptions}
                  value={
                    productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.ozelProductName)?.id)) ||
                    (newOrder.ozelProductName ? { value: newOrder.ozelProductName, label: newOrder.ozelProductName } : null)
                  }
                  onChange={(option) => {
                    if (!option) {
                      onFieldChange('ozelProductName', '');
                      onFieldChange('ozelUnitPrice', '');
                    } else if (option.__isNew__) {
                      onFieldChange('ozelProductName', option.value);
                      onFieldChange('ozelUnitPrice', '');
                    } else {
                      handleOzelProductSelect(option.value);
                    }
                  }}
                  isDisabled={isLoading}
                  isClearable
                  isSearchable
                  placeholder="Ürün seçin veya yazın..."
                  noOptionsMessage={() => "Ürün bulunamadı"}
                  formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                  styles={customSelectStyles}
                />
              </div>

              <div className="form-group">
                <label>Özel Ürün Varyantı</label>
                <input
                  type="text"
                  value={newOrder.ozelProductVariant || ''}
                  onChange={(e) => onFieldChange('ozelProductVariant', e.target.value)}
                  disabled={isLoading}
                  placeholder="Renk, desen vb. giriniz..."
                />
              </div>

              <div className="form-group">
                <label>En (cm)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newOrder.ozelWidth || ''}
                  onKeyDown={handleNumericKeyDown}
                  onChange={(e) => onFieldChange('ozelWidth', e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>Boy (cm)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newOrder.ozelHeight || ''}
                  onKeyDown={handleNumericKeyDown}
                  onChange={(e) => onFieldChange('ozelHeight', e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>Birim Fiyat (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newOrder.ozelUnitPrice || ''}
                  onKeyDown={handleNumericKeyDown}
                  onChange={(e) => onFieldChange('ozelUnitPrice', e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label>Pile Modeli</label>
                <select
                  value={newOrder.ozelPileModel || ''}
                  onChange={(e) => {
                    const selectedModel = e.target.value;
                    onFieldChange('ozelPileModel', selectedModel);

                    // ozel product type'ı belirle
                    const selectedProduct = productItems.find(p => p.name === newOrder.ozelProductName);
                    const ozelProductType = selectedProduct ? selectedProduct.productType : 'tul';
                    const ozelKapsulBirimFiyat = getKapsulUnitPrice(adminSettings, ozelProductType);

                    if (selectedModel === 'Kapsül Pile' && ozelKapsulBirimFiyat) {
                      onFieldChange('ozelCapsuleUnitPrice', ozelKapsulBirimFiyat);
                    } else {
                      onFieldChange('ozelCapsuleUnitPrice', '');
                    }

                    if (newOrder.ozelBaseUnitPrice !== undefined) {
                      const extra = getPileExtraPrice(adminSettings, ozelProductType, selectedModel);
                      onFieldChange('ozelUnitPrice', Number(newOrder.ozelBaseUnitPrice) + extra);
                    }
                  }}
                  disabled={isLoading}
                >
                  <option value="">Pile modeli seçin</option>
                  {localPileModels.map((model) => (
                    <option key={model.id} value={model.modelName}>
                      {model.modelName}
                    </option>
                  ))}
                </select>
              </div>

              {newOrder.ozelPileModel && (
                <div className="form-group">
                  <label>Pile Sıklığı</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={newOrder.ozelPileDensity || ''}
                    onChange={(e) => onFieldChange('ozelPileDensity', e.target.value)}
                    disabled={isLoading}
                    placeholder="Pile sıklığı"
                  />
                </div>
              )}

              {newOrder.ozelWidth && newOrder.ozelUsedM2 && (
                <div className="form-group">
                  <label>Kullanılan Mt (Özel)</label>
                  <input
                    type="text"
                    value={newOrder.ozelUsedM2}
                    readOnly
                    className="readonly-field"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Ek Ücret (Özel) (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newOrder.ozelExtraPrice || ''}
                  onKeyDown={handleNumericKeyDown}
                  onChange={(e) => onFieldChange('ozelExtraPrice', e.target.value)}
                  disabled={isLoading}
                />
              </div>

              {newOrder.ozelPileModel === 'Kapsül Pile' && (
                <>
                  <div className="form-group">
                    <label>Kapsül Birim Fiyat (Özel) (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.ozelCapsuleUnitPrice && newOrder.ozelCapsuleUnitPrice !== 0 && newOrder.ozelCapsuleUnitPrice !== '0' ? newOrder.ozelCapsuleUnitPrice : ''}
                      onChange={(e) => onFieldChange('ozelCapsuleUnitPrice', e.target.value)}
                      disabled={isLoading}
                      placeholder="0.00"
                    />
                  </div>

                  {newOrder.ozelWidth && newOrder.ozelCapsulePrice && newOrder.ozelCapsulePrice !== 0 && newOrder.ozelCapsulePrice !== '0' && (
                    <div className="form-group">
                      <label>Kapsül Ücreti (Özel) (₺)</label>
                      <input
                        type="text"
                        value={formatPrice(newOrder.ozelCapsulePrice)}
                        readOnly
                        className="readonly-field"
                      />
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <label>Adet (Özel)</label>
                <input
                  type="number"
                  min="1"
                  value={newOrder.ozelQuantity || ''}
                  onKeyDown={handleNumericKeyDown}
                  onChange={(e) => onFieldChange('ozelQuantity', e.target.value)}
                  disabled={isLoading}
                  placeholder="Adet giriniz"
                />
              </div>

              {(newOrder.ozelTotalPrice || calculateOzelTasarimTotal(newOrder) > 0) && (
                <div className="form-group">
                  <label>Özel Tasarım Toplam Tutar (₺)</label>
                  <input
                    type="text"
                    value={formatPrice(newOrder.ozelTotalPrice || calculateOzelTasarimTotal(newOrder))}
                    readOnly
                    className="readonly-field total-price"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ASTAR ACCORDION — Sadece Kanat siparişlerinde */}
      {!isTul && (
        <div style={{ gridColumn: '1 / -1', marginTop: '10px', marginBottom: '10px' }}>
          <div
            onClick={() => onFieldChange('hasAstar', !isAstarActive)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 15px',
              backgroundColor: isAstarActive ? '#0f766e' : '#f1f5f9',
              color: isAstarActive ? 'white' : '#1e293b',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-layer-group"></i>
              <span>Astar</span>
            </div>
            <i className={`fas fa-chevron-${isAstarActive ? 'up' : 'down'}`}></i>
          </div>

          {isAstarActive && (
            <div
              className="dynamic-fields"
              style={{
                padding: '20px 15px',
                border: '1px solid #cbd5e1',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                backgroundColor: '#fff',
                marginTop: 0
              }}
            >
              <div className="form-group">
                <label>Astar Ürün Adı</label>
                <CreatableSelect
                  options={astarProductOptions}
                  value={
                    astarProductOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.astarProductName)?.id)) ||
                    (newOrder.astarProductName ? { value: newOrder.astarProductName, label: newOrder.astarProductName } : null)
                  }
                  onChange={(option) => {
                    if (!option) {
                      onFieldChange('astarProductName', '');
                      onFieldChange('astarUnitPrice', '');
                    } else if (option.__isNew__) {
                      onFieldChange('astarProductName', option.value);
                      onFieldChange('astarUnitPrice', '');
                    } else {
                      handleAstarProductSelect(option.value);
                    }
                  }}
                  isDisabled={isLoading}
                  isClearable
                  isSearchable
                  placeholder="Kanat ürünü seçin veya yazın..."
                  noOptionsMessage={() => 'Ürün bulunamadı'}
                  formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
                  styles={customSelectStyles}
                />
              </div>

              <div className="form-group">
                <label>Astar Ürün Varyantı</label>
                <input
                  type="text"
                  value={newOrder.astarProductVariant || ''}
                  onChange={(e) => onFieldChange('astarProductVariant', e.target.value)}
                  disabled={isLoading}
                  placeholder="Renk, desen vb. giriniz..."
                />
              </div>

              <div className="form-group">
                <label>Astar Birim Fiyatı (₺/mt)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newOrder.astarUnitPrice || ''}
                  onKeyDown={handleNumericKeyDown}
                  onChange={(e) => onFieldChange('astarUnitPrice', e.target.value)}
                  disabled={isLoading}
                />
              </div>

              {newOrder.usedM2 && newOrder.astarUnitPrice && (
                <div className="form-group">
                  <label>Astar Ücreti (₺)</label>
                  <input
                    type="text"
                    value={formatPrice(newOrder.astarPrice || calculateAstarPrice(newOrder.usedM2, newOrder.astarUnitPrice, kanatQuantity))}
                    readOnly
                    className="readonly-field"
                  />
                  <small>
                    Ana kanat metresi ({newOrder.usedM2} mt) × birim fiyat × {kanatQuantity} adet
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AKSESUARLAR BÖLÜMÜ — Hem Tül Hem Kanat için */}
      <div style={{ gridColumn: '1 / -1', marginTop: '15px', marginBottom: '15px' }}>
        <div 
          className="section-header" 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '10px 15px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px 8px 0 0', 
            border: '1px solid #e2e8f0', 
            borderBottom: 'none' 
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="fas fa-tags" style={{ color: 'var(--primary-600)' }}></i>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Aksesuarlar</h4>
          </div>
          <button
            type="button"
            onClick={handleAddAccessory}
            className="btn-add-accessory"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px', 
              padding: '6px 12px', 
              backgroundColor: 'var(--primary-600)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer', 
              fontSize: '12px', 
              fontWeight: '600' 
            }}
          >
            <i className="fas fa-plus"></i> Aksesuar Ekle
          </button>
        </div>
        
        <div className="section-body" style={{ padding: '15px', border: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px', backgroundColor: '#fff' }}>
          {(!newOrder.accessories || newOrder.accessories.length === 0) ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '13px' }}>Henüz aksesuar eklenmemiş.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {newOrder.accessories.map((acc) => {
                const isMetre = acc.unit === 'metre';
                return (
                  <div 
                    key={acc.id} 
                    style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMetre ? '1.5fr 1fr 0.8fr 0.8fr 1fr 1.2fr 40px' : '1.5fr 1fr 0.8fr 1fr 1.2fr 40px', 
                      gap: '10px', 
                      alignItems: 'center', 
                      padding: '12px', 
                      backgroundColor: '#f8fafc', 
                      borderRadius: '8px',
                      border: '1px solid #f1f5f9'
                    }}
                  >
                    <div className="form-group-mini">
                      <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Aksesuar Adı</label>
                      <input 
                        type="text" 
                        value={acc.name || ''} 
                        onChange={(e) => handleAccessoryChange(acc.id, 'name', e.target.value)} 
                        placeholder="Bordür, Saçak, Püskül vb." 
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '2px solid #e1e5e9', fontSize: '13px' }} 
                      />
                    </div>

                    <div className="form-group-mini">
                      <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Birim Tipi</label>
                      <select
                        value={acc.unit || 'metre'}
                        onChange={(e) => handleAccessoryChange(acc.id, 'unit', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '2px solid #e1e5e9', fontSize: '13px', background: 'white' }}
                      >
                        <option value="metre">Metre</option>
                        <option value="adet">Adet</option>
                      </select>
                    </div>

                    {isMetre && (
                      <div className="form-group-mini">
                        <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Metre</label>
                        <input 
                          type="number" 
                          step="0.01"
                          value={acc.metre || ''} 
                          onChange={(e) => handleAccessoryChange(acc.id, 'metre', e.target.value)} 
                          placeholder="Metre" 
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '2px solid #e1e5e9', fontSize: '13px' }} 
                        />
                      </div>
                    )}

                    <div className="form-group-mini">
                      <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Adet</label>
                      <input 
                        type="number" 
                        min="1" 
                        value={acc.quantity || ''} 
                        onChange={(e) => handleAccessoryChange(acc.id, 'quantity', e.target.value)} 
                        placeholder="Adet" 
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '2px solid #e1e5e9', fontSize: '13px' }} 
                      />
                    </div>

                    <div className="form-group-mini">
                      <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Fiyat (₺)</label>
                      <input 
                        type="number" 
                        step="0.01"
                        value={acc.price || ''} 
                        onChange={(e) => handleAccessoryChange(acc.id, 'price', e.target.value)} 
                        placeholder="Fiyat" 
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '2px solid #e1e5e9', fontSize: '13px' }} 
                      />
                    </div>

                    <div className="form-group-mini">
                      <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Tutar (₺)</label>
                      <input 
                        type="text" 
                        value={formatPrice(acc.total || 0)} 
                        readOnly 
                        style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '2px solid #e1e5e9', backgroundColor: '#f1f5f9', fontSize: '13px', color: '#64748b', fontWeight: '600' }} 
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveAccessory(acc.id)}
                      style={{ 
                        marginTop: '18px', 
                        padding: '8px', 
                        backgroundColor: '#fee2e2', 
                        color: '#ef4444', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        transition: 'all 0.2s',
                        height: '34px',
                        width: '34px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Sil"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>İskonto (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          value={newOrder.discount || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('discount', e.target.value)}
          disabled={isLoading}
        />
      </div>

      {newOrder.productName && (
        <div className="form-group">
          <label>Toplam Tutar (₺)</label>
          <input
            type="text"
            value={formatPrice(newOrder.totalPrice)}
            readOnly
            className="readonly-field total-price"
          />
        </div>
      )}

      {newOrder.discount > 0 && (
        <div className="form-group">
          <label>İndirimli Tutar (₺)</label>
          <input
            type="text"
            value={formatPrice(newOrder.discountedPrice)}
            readOnly
            className="readonly-field discounted-price"
          />
        </div>
      )}

    </>
  );
};

export default TulKanatForm;
